import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import DescriptionIcon from "@mui/icons-material/Description";
import CategoryIcon from "@mui/icons-material/Category";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../services/api";
import { useDispatch } from "react-redux";
import { addChapter, resetAddChapterResult } from "../../State/Chapter/Action";
import { getModuleById } from "../../State/Module/Action";
import { getCourseById } from "../../State/Course/Action";
import { useSelector } from "react-redux";
import { Upload as tus } from "tus-js-client";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const Chapter = () => {
  const navigate = useNavigate();
  const [chapterType, setChapterType] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [chapterTitle, setChapterTitle] = useState("");
  const [chapterDescription, setChapterDescription] = useState("");
  const [previewAvailable, setPreviewAvailable] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState("");
  const [loading, setLoading] = useState(false); // added loading state
  const [uploadRetryCount, setUploadRetryCount] = useState(0);
  const { moduleId } = useParams();
  const dispatch = useDispatch();
  const [progress, setProgress] = useState(0);
  const addChapterResult = useSelector(
    (store) => store.chapter?.addChapterResult
  );
  const moduleData = useSelector((store) => store.module?.moduleByIdResponse);
  const courseData = useSelector((store) => store.course?.course);

  const handleFileChange = async (event) => {
    if (validateForm()) {
      const file = event.target.files[0];
      if (file) {
        // Check file size (limit to 2GB)
        const maxFileSize = 2 * 1024 * 1024 * 1024; // 2GB in bytes
        if (file.size > maxFileSize) {
          setErrorDialogMessage(
            `File size (${(file.size / (1024 * 1024 * 1024)).toFixed(2)}GB) exceeds the maximum allowed size of 2GB. Please choose a smaller file.`
          );
          setErrorDialogOpen(true);
          return;
        }

        // Check file type
        if (!file.type.startsWith('video/')) {
          setErrorDialogMessage("Please select a valid video file.");
          setErrorDialogOpen(true);
          return;
        }

        setVideoFile(file);
        setUploadRetryCount(0); // Reset retry count for new file
        const formData = new FormData();
        // formData.append("video", file);
        formData.append("title", chapterTitle);
        formData.append("description", chapterDescription);
        formData.append("type", chapterType);
        formData.append("moduleId", moduleId);
        formData.append("previewAvailable", previewAvailable);

        try {
          setLoading(true); // show progress
          
          // const response = await api.post("/chapters/video", formData, {
          const response = await api.post(
            "/chapters/init-video-upload",
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
              timeout: 1800000, // 30 minutes for video uploads
              maxContentLength: Infinity,
              maxBodyLength: Infinity,
            }
          );

          if (response.status === 200 && response.data.success) {
            const {
              videoId,
              libraryId,
              collectionId,
              authorizationSignature,
              authorizationExpiry,
              tusUploadUrl,
              chapterId
            } = response.data.data;
            
            
            // Validate the authorization expiry timestamp
            const expiryTime = new Date(parseInt(authorizationExpiry) * 1000);
            const currentTime = new Date();
            const timeUntilExpiry = expiryTime.getTime() - currentTime.getTime();
            
            
            if (timeUntilExpiry < 0) {
              setErrorDialogMessage("Upload authorization has expired. Please try uploading again.");
              setErrorDialogOpen(true);
              setLoading(false);
              return;
            }
            
            if (timeUntilExpiry < 300) { // Less than 5 minutes remaining
              // Upload authorization expires soon
            }
            
            // Now upload the video file to the obtained uploadUrl
            // 2. Start tus upload directly to Bunny
            var upload = new tus(file, {
              endpoint: tusUploadUrl,
              retryDelays: [0, 3000, 5000, 10000, 20000, 60000, 60000],
              metadata: {
                filetype: file.type,
                filename: file.name,
                collection: collectionId,
              },
              headers: {
                AuthorizationSignature: authorizationSignature,
                AuthorizationExpire: authorizationExpiry,
                LibraryId: libraryId,
                VideoId: videoId,
              },
              onError: (error) => {
                console.error("Upload failed:", error);
                setLoading(false);
                setProgress(0);
                
                // Enhanced error handling with detailed logging
                let errorMessage = "Upload failed. ";
                
                if (error.originalResponse) {
                  const status = error.originalResponse.getStatus();
                  const responseBody = error.originalResponse.getBody();
                  
                  
                  if (status === 401) {
                    errorMessage += "Authentication failed (401). This could be due to: ";
                    errorMessage += "1) Invalid or expired authorization signature, ";
                    errorMessage += "2) Incorrect library ID or video ID, ";
                    errorMessage += "3) API key permissions issue. ";
                    errorMessage += "Please check the console logs for details and try uploading again.";
                  } else if (status === 413) {
                    errorMessage += "File too large. Please try with a smaller file.";
                  } else if (status >= 500) {
                    errorMessage += "Server error. Please try again later.";
                  } else {
                    errorMessage += `Server returned status ${status}. Response: ${responseBody || 'No response body'}`;
                  }
                } else {
                  errorMessage += error.message || "Unknown error occurred.";
                }
                
                setErrorDialogMessage(errorMessage);
                setErrorDialogOpen(true);
              },
              onProgress: (bytesUploaded, bytesTotal) => {
                const progressPercent = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
                setProgress(parseFloat(progressPercent));
              },
              onSuccess: async () => {
                setLoading(false);
                setProgress(100);

                // 👉 notify backend upload is done
                try {
                  await api.post(`/chapters/${chapterId}/finalize-video-upload`);
                } catch (error) {
                  console.error("Failed to finalize upload:", error);
                }

                // Navigate to add-new with courseId if available
                if (moduleData?.courseId) {
                  navigate(`/add-new/${moduleId}?courseId=${moduleData.courseId}`);
                } else {
                  navigate("/add-new/" + moduleId);
                }
              },
            });

            // Check if there are any previous uploads to continue.
            upload.findPreviousUploads().then(function (previousUploads) {
              // Found previous uploads so we select the first one.
              if (previousUploads.length) {
                upload.resumeFromPreviousUpload(previousUploads[0]);
              }

              // Start the upload
              upload.start();
            });
          } else {
            console.error("Upload initialization failed:", response.data);
            setErrorDialogMessage(
              response.data.message || "Failed to initialize video upload. Please try again."
            );
            setErrorDialogOpen(true);
            setLoading(false);
          }
        } catch (error) {
          console.error("Upload initialization error:", error);
          setLoading(false);
          
          if (error.code === "ECONNABORTED") {
            setErrorDialogMessage(
              "Upload timeout. Please try again with a smaller file or check your connection."
            );
          } else if (error.response?.status === 401) {
            setErrorDialogMessage(
              "Authentication failed. Please log in again and try uploading."
            );
          } else if (error.response?.status === 413) {
            setErrorDialogMessage(
              "File too large. Please try with a smaller video file."
            );
          } else if (error.response?.data?.message) {
            setErrorDialogMessage(error.response.data.message);
          } else {
            setErrorDialogMessage("Network error: " + (error.message || "Unknown error occurred"));
          }
          setErrorDialogOpen(true);
        }
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!chapterTitle || chapterTitle.length < 5 || chapterTitle.length > 128) {
      newErrors.chapterTitle =
        "Chapter title must be between 5 and 128 characters.";
    }

    if (
      !chapterDescription ||
      chapterDescription.length < 20 ||
      chapterDescription.length > 1000
    ) {
      newErrors.chapterDescription =
        "Description must be between 20 and 1000 characters.";
    }

    setErrors(newErrors);
    const errorMessages = Object.values(newErrors);
    if (errorMessages.length > 0) {
      setErrorDialogMessage(errorMessages[0]);
      setErrorDialogOpen(true);
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      navigate("/module");
    }
  };

  const handleCreateQuiz = () => {
    if (validateForm()) {
      var data = {
        title: chapterTitle,
        description: chapterDescription,
        type: chapterType,
        previewAvailable: previewAvailable,
      };
      dispatch(addChapter(moduleId, data));
    }
  };

  const handleCreateArticle = () => {
    if (validateForm()) {
      var data = {
        title: chapterTitle,
        description: chapterDescription,
        type: chapterType,
        previewAvailable: previewAvailable,
      };
      dispatch(addChapter(moduleId, data));
    }
  };

  useEffect(() => {
    if (addChapterResult?.data?.success) {
      var chapterId = addChapterResult.data.id;
      if (chapterType === "ARTICLE") {
        navigate(`/add-article/${chapterId}?moduleId=${moduleId}`);
      } else if (chapterType === "QUIZ") {
        navigate(`/add-quiz/${chapterId}?moduleId=${moduleId}`);
      }
    } else if (addChapterResult?.success === false) {
      setErrorDialogMessage(
        addChapterResult.message || "Failed to create chapter."
      );
      setErrorDialogOpen(true);
    }
  }, [addChapterResult, navigate, chapterType, moduleId]);

  // Reset add chapter result when component mounts to prevent auto-redirect
  useEffect(() => {
    dispatch(resetAddChapterResult());
  }, [dispatch]);

  // Fetch module data when component mounts
  useEffect(() => {
    if (moduleId) {
      dispatch(getModuleById(moduleId));
    }
  }, [moduleId, dispatch]);

  // Fetch course data when module data is available
  useEffect(() => {
    if (moduleData?.courseId) {
      dispatch(getCourseById(moduleData.courseId));
    }
  }, [moduleData?.courseId, dispatch]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        p: 3,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 100 100\"><defs><pattern id=\"grain\" width=\"100\" height=\"100\" patternUnits=\"userSpaceOnUse\"><circle cx=\"25\" cy=\"25\" r=\"1\" fill=\"rgba(255,255,255,0.05)\"/><circle cx=\"75\" cy=\"75\" r=\"1\" fill=\"rgba(255,255,255,0.05)\"/></pattern></defs><rect width=\"100\" height=\"100\" fill=\"url(%23grain)\"/></svg>')",
          opacity: 0.3,
        }
      }}
    >
      {/* Course and Module Information - Above the white box */}
      {(courseData?.name || moduleData?.moduleTitle) && (
        <Box 
          mb={4} 
          sx={{ 
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            p: 4, 
            borderRadius: 4,
            boxShadow: "0 20px 40px rgba(0,0,0,0.1), 0 0 0 1px rgba(255,255,255,0.2)",
            width: "100%",
            maxWidth: 600,
            border: "1px solid rgba(255,255,255,0.3)",
          }}
        >
          {courseData?.name && (
            <Typography 
              variant="h4" 
              sx={{ 
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontWeight: "700",
                mb: 1.5,
                letterSpacing: "-0.02em",
              }}
            >
              {courseData.name}
            </Typography>
          )}
          {moduleData?.moduleTitle && (
            <Typography 
              variant="body1" 
              sx={{
                color: "#64748b",
                fontWeight: "500",
                fontSize: "1.1rem",
              }}
            >
              📚 {moduleData.moduleTitle}
            </Typography>
          )}
        </Box>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 5,
          borderRadius: 4,
          width: "100%",
          maxWidth: 600,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(20px)",
          boxShadow: "0 20px 40px rgba(0,0,0,0.1), 0 0 0 1px rgba(255,255,255,0.2)",
          border: "1px solid rgba(255,255,255,0.3)",
        }}
      >

        <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              mr: 2,
              backgroundColor: "rgba(102, 126, 234, 0.1)",
              color: "#667eea",
              "&:hover": {
                backgroundColor: "rgba(102, 126, 234, 0.2)",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h4"
            sx={{ 
              fontWeight: "700",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              display: "flex", 
              alignItems: "center", 
              gap: 2,
              letterSpacing: "-0.02em",
            }}
          >
            <Box sx={{ 
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: 2,
              p: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
              <MenuBookIcon sx={{ color: "white", fontSize: "1.5rem" }} />
            </Box>
            Add New Chapter
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Box mb={4}>
            <TextField
              fullWidth
              variant="outlined"
              label="Chapter Title"
              name="chapterTitle"
              value={chapterTitle}
              onChange={(e) => setChapterTitle(e.target.value)}
              error={Boolean(errors.chapterTitle)}
              helperText={errors.chapterTitle}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  },
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.1)',
                  },
                  '&:hover fieldset': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#64748b',
                  fontWeight: '500',
                },
              }}
            />
          </Box>

          <Box mb={4}>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              label="Description"
              name="chapterDescription"
              value={chapterDescription}
              onChange={(e) => setChapterDescription(e.target.value)}
              error={Boolean(errors.chapterDescription)}
              helperText={
                errors.chapterDescription || 
                `${chapterDescription.length}/1000 characters`
              }
              inputProps={{
                maxLength: 1000
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  },
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.1)',
                  },
                  '&:hover fieldset': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#64748b',
                  fontWeight: '500',
                },
                '& .MuiFormHelperText-root': {
                  color: chapterDescription.length > 800 ? '#f59e0b' : 
                         chapterDescription.length > 900 ? '#ef4444' : '#64748b',
                  fontWeight: '500',
                  fontSize: '0.875rem',
                },
              }}
            />
          </Box>

          <Box mb={4}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: '#64748b', fontWeight: '500' }}>Chapter Type</InputLabel>
              <Select
                value={chapterType}
                label="Chapter Type"
                onChange={(e) => {
                  setChapterType(e.target.value);
                  setVideoFile(null);
                }}
                sx={{
                  borderRadius: 3,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  },
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.1)',
                  },
                  '&:hover fieldset': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                    borderWidth: 2,
                  },
                }}
              >
                <MenuItem value="VIDEO">🎥 Video</MenuItem>
                <MenuItem value="ARTICLE">📄 Article</MenuItem>
                <MenuItem value="QUIZ">🧩 Quiz</MenuItem>
              </Select>
            </FormControl>
            {chapterType === "VIDEO" && (
              <Typography
                variant="body2"
                sx={{
                  color: '#f59e0b',
                  fontWeight: '500',
                  fontSize: '0.875rem',
                  mt: 1,
                  ml: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                ⚠️ Max File size is 2 GB
              </Typography>
            )}
          </Box>

          {/* Preview Available Checkbox */}
          <Box mb={4}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              p: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              borderRadius: 3,
              border: '1px solid rgba(0, 0, 0, 0.1)',
              backdropFilter: 'blur(10px)',
            }}>
              <input
                type="checkbox"
                id="previewAvailable"
                checked={previewAvailable}
                onChange={(e) => setPreviewAvailable(e.target.checked)}
                style={{
                  width: '20px',
                  height: '20px',
                  accentColor: '#667eea',
                }}
              />
              <label 
                htmlFor="previewAvailable"
                style={{
                  color: '#374151',
                  fontWeight: '500',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  flex: 1,
                }}
              >
                👁️ Preview Available
              </label>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: '#6b7280',
                fontSize: '0.875rem',
                mt: 1,
                ml: 1,
              }}
            >
              Allow learners to preview this chapter before purchasing the course
            </Typography>
          </Box>

          {chapterType === "VIDEO" && (
            <Box mb={4}>
              <Button
                fullWidth
                component="label"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                sx={{
                  borderRadius: 3,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  border: '2px dashed #667eea',
                  color: '#667eea',
                  '&:hover': {
                    backgroundColor: 'rgba(102, 126, 234, 0.1)',
                    border: '2px dashed #5a67d8',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                📤 Upload Video
                <VisuallyHiddenInput
                  type="file"
                  accept="video/*"
                  onChange={handleFileChange}
                />
              </Button>
              {videoFile && (
                <Box sx={{ 
                  mt: 2, 
                  p: 2, 
                  backgroundColor: 'rgba(102, 126, 234, 0.1)', 
                  borderRadius: 2,
                  border: '1px solid rgba(102, 126, 234, 0.2)',
                }}>
                  <Typography variant="body2" sx={{ color: '#667eea', fontWeight: '500' }}>
                    📁 {videoFile.name}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {loading && (
            <Box mb={4} sx={{ 
              p: 3, 
              backgroundColor: 'rgba(102, 126, 234, 0.1)', 
              borderRadius: 3,
              border: '1px solid rgba(102, 126, 234, 0.2)',
            }}>
              <Typography variant="body1" sx={{ color: '#667eea', fontWeight: '600', mb: 2 }}>
                📤 Uploading video... {progress}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ 
                  height: 12, 
                  borderRadius: 6,
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 6,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }
                }} 
              />
            </Box>
          )}

          {chapterType === "ARTICLE" && (
            <Box mb={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  handleCreateArticle();
                }}
                sx={{
                  borderRadius: 3,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 25px rgba(102, 126, 234, 0.4)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                📄 Create Article
              </Button>
            </Box>
          )}

          {chapterType === "QUIZ" && (
            <Box mb={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  handleCreateQuiz();
                }}
                sx={{
                  borderRadius: 3,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 25px rgba(102, 126, 234, 0.4)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                🧩 Create Quiz
              </Button>
            </Box>
          )}
        </form>
      </Paper>

      <Dialog 
        open={errorDialogOpen} 
        onClose={() => setErrorDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          }
        }}
      >
        <DialogTitle
          sx={{ 
            display: "flex", 
            alignItems: "center", 
            gap: 2, 
            color: "#ef4444",
            fontWeight: '600',
            fontSize: '1.2rem',
          }}
        >
          <Box sx={{ 
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            borderRadius: 2,
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <ErrorOutlineIcon sx={{ color: 'white' }} />
          </Box>
          {errorDialogMessage.includes("Upload failed") ? "Upload Error" : "Validation Error"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ 
            color: '#64748b',
            lineHeight: 1.6,
            fontSize: '1rem',
          }}>
            {errorDialogMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ gap: 2, p: 3 }}>
          <Button 
            onClick={() => setErrorDialogOpen(false)} 
            sx={{
              borderRadius: 3,
              px: 3,
              py: 1,
              fontWeight: '600',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              '&:hover': {
                backgroundColor: '#e5e7eb',
              }
            }}
          >
            Close
          </Button>
          {errorDialogMessage.includes("Upload failed") && uploadRetryCount < 3 && (
            <Button 
              onClick={() => {
                setErrorDialogOpen(false);
                if (videoFile) {
                  // Retry upload
                  setUploadRetryCount(prev => prev + 1);
                  handleFileChange({ target: { files: [videoFile] } });
                }
              }} 
              sx={{
                borderRadius: 3,
                px: 3,
                py: 1,
                fontWeight: '600',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                }
              }}
            >
              🔄 Retry Upload ({uploadRetryCount + 1}/3)
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Chapter;
